import { Copy, Check, ExternalLink, Facebook, Instagram, Youtube, Video, Search, Twitter, Megaphone, Key } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useI18n, useL } from "../i18n";

// Owner marketing playbook — ready-to-use ad copy, channel guides, hashtags & captions.
export default function PromotePage() {
  const { t, lang } = useI18n();
  const L = useL();

  const channels = [
    {
      key: "meta",
      name: "Meta Ads (Facebook + Instagram)",
      icon: Facebook,
      color: "#1877F2",
      url: "https://business.facebook.com/",
      budget: "$5-15/day",
      goal_ar: "زيارات الموقع + تنزيل التطبيق",
      goal_en: "Website visits + app installs",
      goal_fr: "Visites du site + installations",
      audience_ar: "المسافرون من الخليج + الدول العربية، اهتمامات: السفر، الفنادق، Booking.com",
      audience_en: "Travelers from Gulf + Arab countries, interests: travel, hotels, Booking.com",
      audience_fr: "Voyageurs du Golfe + pays arabes, intérêts : voyage, hôtels, Booking.com",
      how_ar: "1. أنشئ حساب Meta Business Suite\n2. اختر 'حملة زيارات الموقع'\n3. حمّل الصور من مجلد الوجهات\n4. استخدم النصوص الجاهزة أدناه\n5. استهدف المدن (دبي، الرياض، القاهرة، الدار البيضاء)",
      how_en: "1. Create Meta Business Suite account\n2. Choose 'Website traffic' campaign\n3. Upload destination images\n4. Use the ready-made ad copy below\n5. Target major cities (Dubai, Riyadh, Cairo, Casablanca)",
      how_fr: "1. Créez un compte Meta Business Suite\n2. Choisissez la campagne 'Trafic web'\n3. Téléchargez les images des destinations\n4. Utilisez les textes prêts ci-dessous\n5. Ciblez les grandes villes (Dubaï, Riyad, Le Caire, Casablanca)",
    },
    {
      key: "tiktok",
      name: "TikTok Ads",
      icon: Video,
      color: "#000",
      url: "https://ads.tiktok.com/",
      budget: "$20-50/day",
      goal_ar: "مقاطع فيديو قصيرة تُظهر أسعار الوجهات + برج خليفة + البتراء",
      goal_en: "Short videos showing destination prices + Burj Khalifa + Petra",
      goal_fr: "Vidéos courtes montrant les prix + Burj Khalifa + Pétra",
      audience_ar: "أعمار 18-34، مهتمون بالسفر والفنادق والطيران",
      audience_en: "Ages 18-34, interested in travel, hotels, flights",
      audience_fr: "18-34 ans, intéressés par voyage, hôtels, vols",
      how_ar: "1. أنشئ حساب TikTok Ads Manager\n2. اختر هدف 'التحويل' أو 'الزيارات'\n3. أنشئ 3-5 مقاطع فيديو (كل واحد 15-30 ثانية)\n4. استخدم صوت TikTok ترند\n5. استخدم الهاشتاقات الجاهزة أدناه",
      how_en: "1. Create TikTok Ads Manager account\n2. Choose 'Conversions' or 'Traffic' objective\n3. Create 3-5 short videos (15-30s each)\n4. Use trending TikTok audio\n5. Use ready-made hashtags below",
      how_fr: "1. Créez un compte TikTok Ads Manager\n2. Choisissez l'objectif 'Conversion' ou 'Trafic'\n3. Créez 3-5 vidéos courtes (15-30s)\n4. Utilisez des sons tendance\n5. Utilisez les hashtags prêts ci-dessous",
    },
    {
      key: "google",
      name: "Google Ads (Search + YouTube)",
      icon: Search,
      color: "#4285F4",
      url: "https://ads.google.com/",
      budget: "$10-30/day",
      goal_ar: "استهداف الأشخاص الذين يبحثون عن 'رحلة إلى دبي، سعر فندق في القاهرة'",
      goal_en: "Target searchers for 'trip to Dubai, hotel price Cairo'",
      goal_fr: "Cibler les recherches 'voyage Dubaï, prix hôtel Le Caire'",
      audience_ar: "الكلمات المفتاحية: 'أسعار السياحة'، 'حجز فنادق'، 'رحلة إلى...'",
      audience_en: "Keywords: 'travel prices', 'hotel booking', 'trip to...'",
      audience_fr: "Mots-clés : 'prix voyage', 'réservation hôtel', 'voyage à...'",
      how_ar: "1. أنشئ حساب Google Ads\n2. حملة 'البحث' مع الكلمات المفتاحية العربية والإنجليزية\n3. اربطها بـ Google Analytics لتتبع التحويلات\n4. جرّب حملة YouTube بمقاطع 15 ثانية",
      how_en: "1. Create Google Ads account\n2. Search campaign with Arabic + English keywords\n3. Connect Google Analytics for conversion tracking\n4. Try YouTube 15s bumper ads",
      how_fr: "1. Créez un compte Google Ads\n2. Campagne 'Recherche' avec mots-clés multilingues\n3. Connectez Google Analytics pour suivre les conversions\n4. Essayez YouTube 15s",
    },
    {
      key: "x",
      name: "X (Twitter) Ads",
      icon: Twitter,
      color: "#000",
      url: "https://ads.twitter.com/",
      budget: "$5-10/day",
      goal_ar: "استهداف متابعي حسابات السفر مثل Booking, Wego, Almosafer",
      goal_en: "Target followers of travel accounts (Booking, Wego, Almosafer)",
      goal_fr: "Cibler les abonnés de comptes voyages (Booking, Wego, Almosafer)",
      audience_ar: "منطقة الشرق الأوسط، لغة عربية أو إنجليزية",
      audience_en: "Middle East region, Arabic or English speakers",
      audience_fr: "Région Moyen-Orient, arabophones ou anglophones",
      how_ar: "1. أنشئ حساب معلن على X\n2. حملة 'زيارات الموقع'\n3. ضع صورة وجهة + السعر في التغريدة\n4. استخدم الهاشتاقات: #سياحة #الشرق_الأوسط",
      how_en: "1. Create X advertiser account\n2. 'Website visits' campaign\n3. Post destination image + price\n4. Hashtags: #travel #MiddleEast",
      how_fr: "1. Créez un compte annonceur X\n2. Campagne 'Visites du site'\n3. Publiez image + prix\n4. Hashtags : #voyage #MoyenOrient",
    },
  ];

  const adCopy = [
    {
      title_ar: "إعلان الأسعار (الأكثر جذباً)",
      title_en: "Price ad (highest CTR)",
      title_fr: "Annonce prix (meilleur CTR)",
      text_ar: "🇦🇪 دبي بـ $220/يوم • 🇯🇴 البتراء بـ $120/يوم • 🇲🇦 مراكش بـ $85/يوم\n\nاكتشف كل أسعار الشرق الأوسط في مكان واحد + مساعد ذكي يخطط رحلتك مجاناً 👇\nrahhal.app",
      text_en: "🇦🇪 Dubai $220/day • 🇯🇴 Petra $120/day • 🇲🇦 Marrakech $85/day\n\nAll Middle East travel prices in one place + a free AI planner 👇\nrahhal.app",
      text_fr: "🇦🇪 Dubaï 220 $/jour • 🇯🇴 Pétra 120 $/jour • 🇲🇦 Marrakech 85 $/jour\n\nTous les prix du Moyen-Orient au même endroit + un planificateur IA gratuit 👇\nrahhal.app",
    },
    {
      title_ar: "إعلان الذكاء الاصطناعي",
      title_en: "AI concierge ad",
      title_fr: "Annonce concierge IA",
      text_ar: "لا تعرف من أين تبدأ رحلتك القادمة؟ 🤔\n\nمساعد رحّال الذكي يخطط رحلتك في 30 ثانية:\n• جدول يومي\n• أسعار حقيقية\n• نصائح توفير\n\nمجاناً، بالعربية 👇\nrahhal.app",
      text_en: "No idea where to start your next trip? 🤔\n\nRahhal's AI concierge plans it in 30 seconds:\n• Day-by-day itinerary\n• Real prices\n• Money-saving tips\n\nFree, Arabic-first 👇\nrahhal.app",
      text_fr: "Vous ne savez pas par où commencer ?\n\nLe concierge IA Rahhal planifie tout en 30 s :\n• Itinéraire jour par jour\n• Prix réels\n• Astuces économiques\n\nGratuit 👇\nrahhal.app",
    },
    {
      title_ar: "إعلان قصير للتيك توك/ريلز",
      title_en: "Short-form (TikTok / Reels) hook",
      title_fr: "Format court (TikTok / Reels)",
      text_ar: "POV: تريد السفر بس ما تعرف كم يكلفك 💸\n→ رحّال يعطيك السعر الحقيقي لكل مدينة في الشرق الأوسط + خطة يوم بيوم بالذكاء الاصطناعي.\n\nحمّل التطبيق مجاناً ✨",
      text_en: "POV: you wanna travel but don't know the real cost 💸\n→ Rahhal shows every Middle East city's true price + an AI day-by-day plan.\n\nDownload free ✨",
      text_fr: "POV : envie de voyager sans connaître le vrai coût 💸\n→ Rahhal montre le vrai prix de chaque ville du Moyen-Orient + un plan IA jour par jour.\n\nTéléchargez ✨",
    },
  ];

  const hashtags_ar = "#سياحة #سفر #الشرق_الأوسط #دبي #البتراء #مراكش #القاهرة #ترحال #رحّال #وجهات_عربية #ميزانية_سفر";
  const hashtags_en = "#travel #middleeast #dubai #petra #marrakech #cairo #cappadocia #traveltips #travelbudget #wanderlust #travelgram";
  const hashtags_fr = "#voyage #moyenorient #dubai #petra #marrakech #cairo #voyageurs #astucesvoyage #voyagerpascher";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="promote-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#C85A32] font-semibold">
          {L({ ar: "مركز الترويج", en: "Marketing Hub", fr: "Centre marketing" })}
        </div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl text-[#1C1917] flex items-center gap-3">
          <Megaphone className="h-7 w-7 text-[#C85A32]" />
          {L({ ar: "دليل صاحب التطبيق للترويج", en: "Owner's promotion playbook", fr: "Guide de promotion" })}
        </h1>
        <p className="mt-3 text-[#57534E] max-w-2xl">
          {L({
            ar: "دليل شامل لإطلاق حملات مدفوعة على أهم المنصات — مع نصوص إعلانية جاهزة، ميزانيات مقترحة، وهاشتاقات محسّنة.",
            en: "Complete guide to launching paid campaigns on major platforms — with ready ad copy, suggested budgets, and optimized hashtags.",
            fr: "Guide complet pour lancer des campagnes payantes — avec textes prêts, budgets suggérés et hashtags optimisés."
          })}
        </p>
      </div>

      {/* Channels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {channels.map((c) => {
          const Icon = c.icon;
          const goal = { ar: c.goal_ar, en: c.goal_en, fr: c.goal_fr }[lang] || c.goal_en;
          const audience = { ar: c.audience_ar, en: c.audience_en, fr: c.audience_fr }[lang] || c.audience_en;
          const how = { ar: c.how_ar, en: c.how_en, fr: c.how_fr }[lang] || c.how_en;
          return (
            <div key={c.key} className="rounded-3xl bg-white border border-[#E6DED3] p-6" data-testid={`channel-${c.key}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl grid place-items-center text-white" style={{ background: c.color }}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-[#1C1917]">{c.name}</h3>
                    <div className="text-xs text-[#78716C]">{L({ ar: "ميزانية مقترحة", en: "Suggested budget", fr: "Budget suggéré" })}: <span className="font-medium text-[#C85A32]">{c.budget}</span></div>
                  </div>
                </div>
                <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-[#78716C] hover:text-[#C85A32]">
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
              <div className="mt-4 space-y-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#78716C]">{L({ ar: "الهدف", en: "Goal", fr: "Objectif" })}</div>
                  <p className="text-[#1C1917] mt-1">{goal}</p>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#78716C]">{L({ ar: "الجمهور", en: "Audience", fr: "Audience" })}</div>
                  <p className="text-[#57534E] mt-1">{audience}</p>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#78716C]">{L({ ar: "خطوة بخطوة", en: "Step by step", fr: "Étape par étape" })}</div>
                  <pre className="text-[#57534E] mt-1 whitespace-pre-wrap font-sans text-sm">{how}</pre>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Ad copy */}
      <div className="mt-10">
        <h2 className="font-display text-2xl text-[#1C1917] mb-4">
          {L({ ar: "نصوص إعلانية جاهزة للاستخدام", en: "Ready-to-use ad copy", fr: "Textes publicitaires prêts" })}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {adCopy.map((a, i) => {
            const text = { ar: a.text_ar, en: a.text_en, fr: a.text_fr }[lang] || a.text_en;
            const title = { ar: a.title_ar, en: a.title_en, fr: a.title_fr }[lang] || a.title_en;
            return <AdCopyCard key={i} title={title} text={text} testid={`adcopy-${i}`} />;
          })}
        </div>
      </div>

      {/* Hashtags */}
      <div className="mt-10">
        <h2 className="font-display text-2xl text-[#1C1917] mb-4">
          {L({ ar: "هاشتاقات محسّنة", en: "Optimized hashtags", fr: "Hashtags optimisés" })}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <HashtagCard label="AR" text={hashtags_ar} />
          <HashtagCard label="EN" text={hashtags_en} />
          <HashtagCard label="FR" text={hashtags_fr} />
        </div>
      </div>

      <div className="mt-10 rounded-3xl bg-gradient-to-br from-[#1C1917] to-[#57534E] text-[#FAF7F2] p-6 lg:p-8">
        <div className="text-[11px] uppercase tracking-widest opacity-70">
          {L({ ar: "ملاحظة مهمة", en: "Important note", fr: "Note importante" })}
        </div>
        <p className="mt-2 text-sm opacity-90 leading-relaxed">
          {L({
            ar: "التطبيق لا يستطيع شراء الإعلانات نيابةً عنك — تحتاج لحساب معلن على كل منصة + بطاقة دفع. لكن كل ما تحتاجه من نصوص وصور وتوجيه استراتيجي موجود هنا. ابدأ بميزانية صغيرة ($5/يوم على Meta)، وراقب النتائج قبل التوسع.",
            en: "The app can't buy ads for you — you'll need an advertiser account on each platform + a payment card. But every piece of copy, image and strategic guidance you need is here. Start with a small budget ($5/day on Meta), then scale what works.",
            fr: "L'application ne peut pas acheter les annonces pour vous — vous avez besoin d'un compte annonceur + une carte de paiement. Mais tous les textes, images et conseils stratégiques sont ici. Commencez petit (5 $/jour sur Meta), puis étendez ce qui fonctionne."
          })}
        </p>
      </div>

      {/* Keys Setup section */}
      <div className="mt-10">
        <h2 className="font-display text-2xl text-[#1C1917] mb-4 flex items-center gap-2">
          <Key className="h-5 w-5 text-[#C85A32]" />
          {L({ ar: "إعداد مفاتيح الأرباح", en: "Revenue keys setup", fr: "Configuration des clés de revenus" })}
        </h2>
        <p className="text-sm text-[#57534E] mb-5 max-w-2xl">
          {L({
            ar: "أضف مفاتيحك في ملف /app/frontend/.env ثم أعد تشغيل الواجهة. حتى بدونها التطبيق يعمل بشكل كامل، لكن الأرباح تبدأ فقط بعد إضافتها.",
            en: "Add your keys to /app/frontend/.env, then restart the frontend. The app works fully without them, but revenue only starts once they're added.",
            fr: "Ajoutez vos clés dans /app/frontend/.env puis redémarrez le frontend. L'app fonctionne sans, mais les revenus démarrent après."
          })}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <KeyCard
            title="Google AdSense"
            color="#4285F4"
            envs={[
              "REACT_APP_ADSENSE_CLIENT=ca-pub-XXXXXXXXXXXXXXXX",
              "REACT_APP_ADSENSE_SLOT_HOME_BANNER=1234567890",
              "REACT_APP_ADSENSE_SLOT_DETAIL_INLINE=2345678901",
              "REACT_APP_ADSENSE_SLOT_SIDEBAR=3456789012",
            ]}
            steps={{
              ar: "1. سجّل في adsense.google.com\n2. أضف موقعك واحصل على publisher ID\n3. أنشئ 3 وحدات إعلانية (Home Banner, In-Article, Sidebar)\n4. الصق المعرفات في .env",
              en: "1. Sign up at adsense.google.com\n2. Add your site and get your publisher ID\n3. Create 3 ad units (Home Banner, In-Article, Sidebar)\n4. Paste the slot IDs into .env",
              fr: "1. Inscrivez-vous sur adsense.google.com\n2. Ajoutez votre site, obtenez le publisher ID\n3. Créez 3 unités publicitaires\n4. Collez les IDs dans .env"
            }}
            link="https://adsense.google.com/"
            L={L}
          />
          <KeyCard
            title="Booking.com Partners"
            color="#003580"
            envs={[
              "REACT_APP_BOOKING_AID=1234567",
              "REACT_APP_AGODA_CID=1234567",
            ]}
            steps={{
              ar: "1. اذهب إلى partners.booking.com\n2. سجّل كشريك (Affiliate Partner)\n3. احصل على معرّف AID\n4. للـ Agoda: افتح partners.agoda.com واحصل على CID",
              en: "1. Go to partners.booking.com\n2. Register as Affiliate Partner\n3. Get your AID\n4. For Agoda: partners.agoda.com and get CID",
              fr: "1. Allez sur partners.booking.com\n2. Inscrivez-vous comme partenaire affilié\n3. Obtenez votre AID\n4. Pour Agoda : partners.agoda.com et CID"
            }}
            link="https://partners.booking.com/"
            L={L}
          />
        </div>
      </div>
    </div>
  );
}

function KeyCard({ title, color, envs, steps, link, L }) {
  const [copied, setCopied] = useState(false);
  const stepText = steps.ar; // placeholder; we render translated below
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(envs.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) { console.warn("clipboard write failed", err); }
  };
  return (
    <div className="rounded-3xl bg-white border border-[#E6DED3] p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl grid place-items-center text-white" style={{ background: color }}>
            <Key className="h-4 w-4" />
          </div>
          <h3 className="font-display text-lg text-[#1C1917]">{title}</h3>
        </div>
        <a href={link} target="_blank" rel="noopener noreferrer" className="text-[#78716C] hover:text-[#C85A32]">
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>
      <StepsBlock steps={steps} L={L} />
      <div className="mt-4 rounded-xl bg-[#1C1917] text-[#FAF7F2] p-3 text-xs font-mono relative">
        {envs.map((e) => <div key={e} className="truncate">{e}</div>)}
        <button
          onClick={copy}
          className="absolute top-2 end-2 h-7 w-7 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}

function StepsBlock({ steps, L }) {
  const text = L(steps);
  return (
    <pre className="mt-4 whitespace-pre-wrap font-sans text-sm text-[#57534E] leading-relaxed">{text}</pre>
  );
}

function AdCopyCard({ title, text, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("✓");
      setTimeout(() => setCopied(false), 1600);
    } catch (err) { console.warn("clipboard write failed", err); }
  };
  return (
    <div className="rounded-2xl bg-white border border-[#E6DED3] p-5 flex flex-col" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#C85A32] font-semibold">{title}</div>
      <pre className="mt-2 flex-1 whitespace-pre-wrap font-sans text-sm text-[#1C1917] leading-relaxed">{text}</pre>
      <button
        onClick={copy}
        className="mt-3 h-9 rounded-full bg-[#1C1917] text-[#FAF7F2] hover:bg-[#C85A32] text-sm inline-flex items-center justify-center gap-2 transition-colors"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function HashtagCard({ label, text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (err) { console.warn("clipboard write failed", err); }
  };
  return (
    <div className="rounded-2xl bg-[#F3EDE4] p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">{label}</div>
        <button onClick={copy} className="text-[#C85A32] hover:text-[#1C1917]">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
      <p className="mt-2 text-sm text-[#1C1917] leading-relaxed">{text}</p>
    </div>
  );
}
